import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate }                             from 'react-router-dom';
import { useAuth }                                 from '../contexts/AuthContext';
import {
  getCategories, generateCategories, listParts,
  generateSubSystemParts, getStatuses, patchPart,
} from '../api/parts';
import './PartsPage.css';

/* ── Transform flat category rows → nested structure ─────────────────────────
   Input row shape (from VEHICLE_categories GET):
     { categoryId, category, category_order, subCategoryId, sub_category,
       sub_category_order, source, part_count }
   Output:
     [ { categoryId, category, category_order, part_count (total),
         sub_categories: [{ subCategoryId, sub_category, sub_category_order, part_count }] } ]
*/
function buildHierarchy(rows) {
  const map = new Map();
  rows.forEach(r => {
    if (!map.has(r.categoryId)) {
      map.set(r.categoryId, {
        categoryId:     r.categoryId,
        category:       r.category,
        category_order: r.category_order,
        part_count:     0,          // summed from sub-categories below
        sub_categories: [],
      });
    }
    if (r.subCategoryId) {
      const cat    = map.get(r.categoryId);
      const count  = parseInt(r.part_count, 10) || 0;
      const already = cat.sub_categories.find(s => s.subCategoryId === r.subCategoryId);
      if (!already) {
        cat.sub_categories.push({
          subCategoryId:      r.subCategoryId,
          sub_category:       r.sub_category,
          sub_category_order: r.sub_category_order,
          part_count:         count,
        });
        cat.part_count += count;    // roll up to parent
      }
    }
  });
  const cats = Array.from(map.values());
  cats.sort((a, b) => a.category_order - b.category_order);
  cats.forEach(c => c.sub_categories.sort((a, b) => a.sub_category_order - b.sub_category_order));
  return cats;
}

/* ── Category icons — fallback to gear if unknown ────────────────────────── */
const CAT_ICONS = {
  'engine':        '🔧',
  'drivetrain':    '⚙️',
  'suspension':    '🛞',
  'brakes':        '🛑',
  'steering':      '🎡',
  'electrical':    '⚡',
  'exhaust':       '💨',
  'body':          '🚘',
  'trim':          '✨',
  'interior':      '💺',
  'wheels':        '🔩',
  'tires':         '🔩',
  'chassis':       '🏗️',
  'decals':        '🏷️',
  'tags':          '🏷️',
  'fuel':          '⛽',
  'cooling':       '🌡️',
  'ignition':      '🔌',
  'default':       '🔩',
};

function categoryIcon(name = '') {
  const lower = name.toLowerCase();
  for (const [key, icon] of Object.entries(CAT_ICONS)) {
    if (lower.includes(key)) return icon;
  }
  return CAT_ICONS.default;
}

/* ── Condition badge colors ──────────────────────────────────────────────── */
const CONDITION_COLORS = {
  'NOS':     '#2a7a2a',
  'NEW':     '#2a7a2a',
  'GOOD':    '#4a6a2a',
  'FAIR':    '#7a6a1a',
  'POOR':    '#7a3a1a',
  'UNKNOWN': '#3a3a3a',
};

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function PartsPage() {
  const { user, signOut }  = useAuth();
  const navigate           = useNavigate();

  /* vehicle from localStorage */
  const [vehicle, setVehicle] = useState(null);

  /* hierarchy data */
  const [hierarchy,    setHierarchy]    = useState([]);   // [ { categoryId, category, sub_categories } ]
  const [loadingCats,  setLoadingCats]  = useState(true);
  const [catError,     setCatError]     = useState('');

  /* drill-down state */
  const [selectedCat,    setSelectedCat]    = useState(null);  // { categoryId, category, sub_categories }
  const [selectedSubCat, setSelectedSubCat] = useState(null);  // { subCategoryId, sub_category }

  /* parts at leaf level */
  const [parts,        setParts]        = useState([]);
  const [loadingParts, setLoadingParts] = useState(false);
  const [partsError,   setPartsError]   = useState('');

  /* AI generate categories modal */
  const [showAiModal,  setShowAiModal]  = useState(false);
  const [generating,   setGenerating]   = useState(false);
  const [genError,     setGenError]     = useState('');

  /* ref data for status dropdown */
  const [statuses, setStatuses] = useState([]);

  /* AI generate parts (sub-system) modal */
  const [showPartsModal,   setShowPartsModal]   = useState(false);
  const [partsModalTarget, setPartsModalTarget] = useState(null); // { category, sub_category }
  const [generatingParts,  setGeneratingParts]  = useState(false);
  const [genPartsError,    setGenPartsError]    = useState('');

  /* ── Load vehicle from localStorage + ref data ──────────────────── */
  useEffect(() => {
    const cached = localStorage.getItem('ar_active_vehicle');
    if (cached) {
      try { setVehicle(JSON.parse(cached)); } catch (_) {}
    }
    // Load status reference list for inline dropdowns
    getStatuses().then(r => setStatuses(r.data || [])).catch(() => {});
  }, []);

  /* ── Load categories whenever vehicle is set ─────────────────────── */
  const loadCategories = useCallback(async (vehicleId) => {
    setLoadingCats(true);
    setCatError('');
    try {
      const res = await getCategories(vehicleId);
      if (res.success && Array.isArray(res.data) && res.data.length) {
        // Only show categories that were generated specifically for this vehicle.
        // The procedure falls back to global rows (source='global') when no
        // vehicle-specific hierarchy exists — treat that as empty so the user
        // is prompted to run AI generation.
        const vehicleRows = res.data.filter(r => r.source === 'vehicle');
        setHierarchy(vehicleRows.length ? buildHierarchy(vehicleRows) : []);
      } else {
        setHierarchy([]);
      }
    } catch (err) {
      setCatError('Could not load categories.');
      setHierarchy([]);
    } finally {
      setLoadingCats(false);
    }
  }, []);

  useEffect(() => {
    if (vehicle?.vehicleId) {
      loadCategories(vehicle.vehicleId);
    }
  }, [vehicle, loadCategories]);

  /* ── Load parts when a sub-category is selected ──────────────────── */
  useEffect(() => {
    if (!selectedSubCat || !vehicle?.vehicleId) return;
    setLoadingParts(true);
    setPartsError('');
    setParts([]);
    listParts(vehicle.vehicleId, selectedSubCat.subCategoryId)
      .then(res => {
        if (res.success) setParts(res.data || []);
        else setPartsError('Could not load parts.');
      })
      .catch(() => setPartsError('Could not load parts.'))
      .finally(() => setLoadingParts(false));
  }, [selectedSubCat, vehicle]);

  /* ── AI generate handler ─────────────────────────────────────────── */
  async function handleAiGenerate() {
    if (!vehicle?.vehicleId) return;
    setGenerating(true);
    setGenError('');
    try {
      const res = await generateCategories(vehicle.vehicleId);
      if (res.success) {
        setShowAiModal(false);
        await loadCategories(vehicle.vehicleId);
      } else {
        setGenError(res.message || 'AI generation failed.');
      }
    } catch (err) {
      setGenError(err?.response?.data?.message || 'AI generation failed. Check the server.');
    } finally {
      setGenerating(false);
    }
  }

  /* ── AI generate parts for a sub-system ─────────────────────────── */
  function openPartsModal(cat, sub, e) {
    e?.stopPropagation();
    setPartsModalTarget({ category: cat, sub_category: sub });
    setGenPartsError('');
    setShowPartsModal(true);
  }

  async function handleGenerateParts() {
    if (!vehicle || !partsModalTarget) return;
    setGeneratingParts(true);
    setGenPartsError('');
    try {
      const payload = {
        year:         vehicle.year,
        make:         vehicle.make,
        model:        vehicle.model,
        variant:      vehicle.trim_package   || undefined,
        engine:       vehicle.engine_desc    || undefined,
        category:     partsModalTarget.category,
        sub_category: partsModalTarget.sub_category,
      };
      const res = await generateSubSystemParts(vehicle.vehicleId, payload);
      if (res.success) {
        setShowPartsModal(false);
        // If already viewing this sub-category, refresh the parts list
        if (selectedSubCat?.sub_category === partsModalTarget.sub_category) {
          const refreshed = await listParts(vehicle.vehicleId, selectedSubCat.subCategoryId);
          if (refreshed.success) setParts(refreshed.data || []);
        }
      } else {
        setGenPartsError(res.message || 'Parts generation failed.');
      }
    } catch (err) {
      setGenPartsError(err?.response?.data?.message || 'Parts generation failed. Check the server.');
    } finally {
      setGeneratingParts(false);
    }
  }

  /* ── Navigation helpers ──────────────────────────────────────────── */
  function goHome()         { navigate('/dashboard'); }
  function handleSignOut()  { signOut(); navigate('/'); }

  function selectCategory(cat) {
    setSelectedCat(cat);
    setSelectedSubCat(null);
    setParts([]);
  }

  function selectSubCategory(sub) {
    setSelectedSubCat(sub);
    setParts([]);
  }

  function goBackToCats() {
    setSelectedCat(null);
    setSelectedSubCat(null);
    setParts([]);
  }

  function goBackToSubCats() {
    setSelectedSubCat(null);
    setParts([]);
  }

  /* ── Derived state ───────────────────────────────────────────────── */
  const vehicleLabel = vehicle
    ? `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim_package ? ' ' + vehicle.trim_package : ''}`.trim()
    : '—';

  const level = selectedSubCat ? 2 : selectedCat ? 1 : 0;

  /* ── Render ──────────────────────────────────────────────────────── */
  return (
    <div className="pts-root">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <header className="pts-header">
        <div className="pts-header-brand">
          <span className="pts-header-gear">⚙</span>
          <span className="pts-header-name">AutoRestoration</span>
        </div>

        <div className="pts-header-vehicle">
          {vehicle && (
            <>
              <span className="pts-vehicle-icon">🚗</span>
              <span className="pts-vehicle-label">{vehicleLabel}</span>
              {vehicle.nickname && (
                <span className="pts-vehicle-nick">"{vehicle.nickname}"</span>
              )}
            </>
          )}
        </div>

        <div className="pts-header-user">
          <span className="pts-username">@{user?.username}</span>
          <button className="pts-signout-btn" onClick={handleSignOut}>Sign Out</button>
        </div>
      </header>

      {/* ── Page body ───────────────────────────────────────────────── */}
      <div className="pts-body">

        {/* ── Back to dashboard ──────────────────────────────────── */}
        <button className="pts-back-dash" onClick={goHome}>
          ← Dashboard
        </button>

        {/* ── Page title + breadcrumb ─────────────────────────────── */}
        <div className="pts-page-title-row">
          <div className="pts-breadcrumb">
            <span
              className={`pts-crumb ${level === 0 ? 'pts-crumb-active' : 'pts-crumb-link'}`}
              onClick={level > 0 ? goBackToCats : undefined}
            >
              🔩 Parts
            </span>
            {selectedCat && (
              <>
                <span className="pts-crumb-sep">›</span>
                <span
                  className={`pts-crumb ${level === 1 ? 'pts-crumb-active' : 'pts-crumb-link'}`}
                  onClick={level > 1 ? goBackToSubCats : undefined}
                >
                  {selectedCat.category}
                </span>
              </>
            )}
            {selectedSubCat && (
              <>
                <span className="pts-crumb-sep">›</span>
                <span className="pts-crumb pts-crumb-active">
                  {selectedSubCat.sub_category}
                </span>
              </>
            )}
          </div>

          {/* AI Generate button — only on the root level */}
          {level === 0 && !loadingCats && (
            <button
              className="pts-ai-btn"
              onClick={() => { setGenError(''); setShowAiModal(true); }}
            >
              🤖 AI Generate Categories
            </button>
          )}
        </div>

        {/* ─────────────────────────────────────────────────────────── */}
        {/* LEVEL 0: Category grid                                      */}
        {/* ─────────────────────────────────────────────────────────── */}
        {level === 0 && (
          <>
            {loadingCats && (
              <div className="pts-loading">Loading categories…</div>
            )}

            {!loadingCats && catError && (
              <div className="pts-error">{catError}</div>
            )}

            {!loadingCats && !catError && hierarchy.length === 0 && (
              <div className="pts-empty">
                <div className="pts-empty-icon">🔩</div>
                <h3 className="pts-empty-title">No categories yet</h3>
                <p className="pts-empty-text">
                  Use the AI Generate button to build a restoration category
                  hierarchy tailored to your {vehicleLabel}.
                </p>
                <button
                  className="pts-ai-btn pts-ai-btn-large"
                  onClick={() => { setGenError(''); setShowAiModal(true); }}
                >
                  🤖 AI Generate Categories
                </button>
              </div>
            )}

            {!loadingCats && hierarchy.length > 0 && (
              <div className="pts-cat-grid">
                {hierarchy.map(cat => (
                  <button
                    key={cat.categoryId}
                    className="pts-cat-card"
                    onClick={() => selectCategory(cat)}
                  >
                    <span className="pts-cat-icon">{categoryIcon(cat.category)}</span>
                    <span className="pts-cat-name">{cat.category}</span>
                    <div className="pts-cat-counts">
                      <span className="pts-cat-sub-count">
                        {cat.sub_categories.length} sub-system{cat.sub_categories.length !== 1 ? 's' : ''}
                      </span>
                      {cat.part_count > 0 && (
                        <span className="pts-cat-part-count">
                          {cat.part_count} part{cat.part_count !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <span className="pts-cat-arrow">›</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* ─────────────────────────────────────────────────────────── */}
        {/* LEVEL 1: Sub-category grid                                  */}
        {/* ─────────────────────────────────────────────────────────── */}
        {level === 1 && selectedCat && (
          <>
            <button className="pts-level-back" onClick={goBackToCats}>
              ← Back to Categories
            </button>

            {selectedCat.sub_categories.length === 0 ? (
              <div className="pts-empty">
                <div className="pts-empty-icon">📂</div>
                <h3 className="pts-empty-title">No sub-categories</h3>
                <p className="pts-empty-text">
                  This category has no sub-systems defined yet.
                </p>
              </div>
            ) : (
              <div className="pts-subcat-grid">
                {selectedCat.sub_categories.map(sub => (
                  <button
                    key={sub.subCategoryId}
                    className="pts-subcat-card"
                    onClick={() => selectSubCategory(sub)}
                  >
                    <span className="pts-subcat-name">{sub.sub_category}</span>
                    <span className="pts-subcat-actions">
                      {sub.part_count > 0 && (
                        <span className="pts-subcat-part-count">
                          {sub.part_count}
                        </span>
                      )}
                      <span
                        className="pts-subcat-ai-btn"
                        title={`Generate parts for ${sub.sub_category}`}
                        onClick={(e) => openPartsModal(selectedCat.category, sub.sub_category, e)}
                      >
                        🤖
                      </span>
                      <span className="pts-subcat-arrow">›</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* ─────────────────────────────────────────────────────────── */}
        {/* LEVEL 2: Parts list                                         */}
        {/* ─────────────────────────────────────────────────────────── */}
        {level === 2 && selectedSubCat && (
          <>
            <button className="pts-level-back" onClick={goBackToSubCats}>
              ← Back to {selectedCat?.category}
            </button>

            {/* AI button — always visible at the top of the parts view */}
            <div className="pts-parts-ai-bar">
              <div className="pts-parts-ai-bar-label">
                <span className="pts-parts-ai-bar-path">
                  {selectedCat?.category} › {selectedSubCat.sub_category}
                </span>
                <span className="pts-parts-ai-bar-hint">
                  AI generates parts scoped to this sub-system only
                </span>
              </div>
              <button
                className="pts-ai-btn"
                onClick={() => openPartsModal(selectedCat?.category, selectedSubCat.sub_category)}
              >
                🤖 Generate Parts
              </button>
            </div>

            {loadingParts && (
              <div className="pts-loading">Loading parts…</div>
            )}

            {!loadingParts && partsError && (
              <div className="pts-error">{partsError}</div>
            )}

            {!loadingParts && !partsError && parts.length === 0 && (
              <div className="pts-empty">
                <div className="pts-empty-icon">📦</div>
                <h3 className="pts-empty-title">No parts yet</h3>
                <p className="pts-empty-text">
                  No parts have been added to{' '}
                  <strong>{selectedSubCat.sub_category}</strong> yet.
                  Hit <strong>Generate Parts</strong> above to have Claude build
                  the list for this sub-system.
                </p>
              </div>
            )}

            {!loadingParts && parts.length > 0 && (
              <div className="pts-parts-section">
                <div className="pts-parts-header-row">
                  <span className="pts-parts-count">
                    {parts.length} part{parts.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="pts-parts-list">
                  {parts.map(p => (
                    <div
                      key={p.partId}
                      className="pts-part-card pts-part-card-clickable"
                      onClick={() => navigate(`/parts/${p.partId}`)}
                    >
                      <div className="pts-part-main">
                        <span className="pts-part-name">{p.name}</span>
                        {p.oem_part_number && (
                          <span className="pts-part-oem">OEM# {p.oem_part_number}</span>
                        )}
                        <span className="pts-part-detail-hint">›</span>
                      </div>
                      <div className="pts-part-meta">
                        {p.condition_code && (
                          <span
                            className="pts-part-condition"
                            style={{ background: CONDITION_COLORS[p.condition_code] || CONDITION_COLORS.UNKNOWN }}
                          >
                            {p.condition_code}
                          </span>
                        )}
                        {/* Inline status change — stop propagation so card click still opens detail */}
                        <select
                          className="pts-part-status-sel"
                          value={p.status_code || ''}
                          onClick={e => e.stopPropagation()}
                          onChange={async e => {
                            const code = e.target.value;
                            e.stopPropagation();
                            setParts(prev => prev.map(x =>
                              x.partId === p.partId ? { ...x, status_code: code } : x
                            ));
                            try { await patchPart(p.partId, { status_code: code }); }
                            catch { /* silent — optimistic update already applied */ }
                          }}
                        >
                          {statuses.map(s => (
                            <option key={s.refStatusId} value={s.code}>{s.name}</option>
                          ))}
                        </select>
                        {p.needs_restoration === 1 && (
                          <span className="pts-part-flag">🔧 Needs Work</span>
                        )}
                        {p.quantity > 1 && (
                          <span className="pts-part-flag">×{p.quantity}</span>
                        )}
                      </div>
                      {p.restoration_notes && (
                        <div className="pts-part-notes">{p.restoration_notes}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* AI CONFIRM MODAL                                               */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* AI PARTS MODAL (sub-system)                                    */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {showPartsModal && partsModalTarget && (
        <div className="pts-modal-overlay" onClick={() => !generatingParts && setShowPartsModal(false)}>
          <div className="pts-modal" onClick={e => e.stopPropagation()}>

            <div className="pts-modal-icon">🔩</div>
            <h2 className="pts-modal-title">Generate Parts — {partsModalTarget.sub_category}</h2>

            <div className="pts-modal-body">
              <p>
                Claude will generate a complete parts list specifically for the{' '}
                <strong>{partsModalTarget.category} &rsaquo; {partsModalTarget.sub_category}</strong>{' '}
                sub-system of your <strong>{vehicleLabel}</strong>.
              </p>
              <ul className="pts-modal-list">
                <li>🔩 Every individual part, seal, gasket, and fastener set</li>
                <li>🏷️ OEM part numbers where known</li>
                <li>📂 Scoped to this sub-system only — no unrelated parts</li>
                <li>⚡ Much faster and smaller than a full-vehicle generation</li>
              </ul>
              <p className="pts-modal-note">
                Parts will be added to your inventory. Existing parts with matching
                names are updated, not duplicated.
              </p>
            </div>

            {genPartsError && (
              <div className="pts-modal-error">{genPartsError}</div>
            )}

            <div className="pts-modal-actions">
              <button
                className="pts-modal-cancel"
                onClick={() => setShowPartsModal(false)}
                disabled={generatingParts}
              >
                ← Back to Parts
              </button>
              <button
                className="pts-modal-confirm"
                onClick={handleGenerateParts}
                disabled={generatingParts}
              >
                {generatingParts ? (
                  <><span className="pts-modal-spinner" /> Generating…</>
                ) : (
                  '🤖 Continue'
                )}
              </button>
            </div>

          </div>
        </div>
      )}

      {showAiModal && (
        <div className="pts-modal-overlay" onClick={() => !generating && setShowAiModal(false)}>
          <div className="pts-modal" onClick={e => e.stopPropagation()}>

            <div className="pts-modal-icon">🤖</div>
            <h2 className="pts-modal-title">AI Category Generation</h2>

            <div className="pts-modal-body">
              <p>
                Claude will analyze your <strong>{vehicleLabel}</strong> and
                build a complete parts category hierarchy tailored to this
                exact vehicle — covering every system needed for a full
                frame-off restoration.
              </p>
              <ul className="pts-modal-list">
                <li>🔧 Top-level systems (Engine, Drivetrain, Body, Interior, etc.)</li>
                <li>📂 Sub-systems specific to your trim and engine</li>
                <li>⚡ Correct restoration terminology for the year and make</li>
              </ul>
              <p className="pts-modal-note">
                This typically takes 5–15 seconds and uses a small amount of
                your AI token allowance. Existing categories will be updated,
                not deleted.
              </p>
            </div>

            {genError && (
              <div className="pts-modal-error">{genError}</div>
            )}

            <div className="pts-modal-actions">
              <button
                className="pts-modal-cancel"
                onClick={() => setShowAiModal(false)}
                disabled={generating}
              >
                ← Back to Parts
              </button>
              <button
                className="pts-modal-confirm"
                onClick={handleAiGenerate}
                disabled={generating}
              >
                {generating ? (
                  <><span className="pts-modal-spinner" /> Generating…</>
                ) : (
                  '🤖 Continue'
                )}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
